// useLayoutPreference — Classic ⇄ Modern の per-user 選択を React state にミラーし、
// viewport (幅・高さ・pointer 特性によるスマホ判定) と合わせて「今どちらを出すか」
// 「トグルを出すか」を返す hook.
//
// 背景 (docs/_wip/modern-layout.md §9.4 / §9.4.1):
//   - 切替の体感: 同一 engine インスタンス上で ClassicLayout ⇄ ModernLayout を
//     差し替えるだけ (engine は React 非依存クラスで SessionProvider が所有) →
//     **canvas / room 状態は保持** のまま即時トグル。レイアウト component は remount
//     されるので per-layout のローカル UI state (選択中ペン幅 cell 等) はリセット
//     されるが、それは許容 (= このスライスの確定スコープ)。
//   - スマホ (小画面) = Modern 固定 + トグル非表示 (§9.4.1 ④)。
//
// store は useRef で 1 インスタンス (= test で storage mock を差し替えやすい単一管轄、
// uiScalePreference の流儀)。明示トグル (setLayout) だけが localStorage に永続する
// (= §9.4.1 ③「明示トグルのみ永続」)。

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createLayoutPreferenceStore,
  type LayoutKind,
  type LayoutPreferenceStore,
} from '../../infrastructure/layoutPreference.js';
import {
  COARSE_POINTER_MEDIA_QUERY,
  isPhoneViewport,
  type PhoneViewportInput,
  resolveLayout,
  shouldShowLayoutToggle,
} from './layoutResolution.js';

export interface UseLayoutPreferenceResult {
  /** 実際に表示するレイアウト (= resolveLayout 結果。スマホは modern 固定). */
  readonly layout: LayoutKind;
  /**
   * 明示トグル. ユーザ選択を localStorage に永続化 (= §9.4.1 ③「明示トグルのみ永続」)。
   * スマホでは layout が常に modern 固定なので呼んでも表示は変わらない (= トグル
   * 自体を非表示にしているので通常呼ばれない) が、保存値は更新される。
   */
  readonly setLayout: (next: LayoutKind) => void;
  /** スマホ (= Modern フォンシェル固定) か。isPhoneViewport (幅 ‖ coarse × 短辺)。 */
  readonly isMobile: boolean;
  /** 切替トグルを表示してよいか (= スマホでは false). */
  readonly showToggle: boolean;
}

export interface UseLayoutPreferenceOptions {
  /** test 用に store を注入する (= storage mock を渡した store)。省略時は browser localStorage。 */
  readonly store?: LayoutPreferenceStore;
  /**
   * レイアウト利用計測 (pen-usage-analytics-2026-08-18.md §9): 明示トグルの瞬間
   * (= `setLayout` が呼ばれたとき) だけ呼ばれるコールバック。切替後の値
   * (`next`) を渡す。
   *
   * **呼ばれないケース (重要)**:
   *   - 毎 render (このコールバックは状態同期ではなくイベント)
   *   - `resolveLayout` によるスマホ強制 Modern 解決 (`isMobile` 変化)。
   *     これはユーザーの選択ではないため — 強制解決を切替として数えると
   *     「モバイルユーザーは全員 Classic → Modern に切り替えた」という
   *     誤ったデータになる (§9.3)。
   *
   * caller (`App.tsx` の `RoomLayout` / `ReJoinScreen.tsx`) が `useSession().transport`
   * を束縛した `reportLayoutSwitch` を渡す想定。ここでは Transport を一切知らない
   * (= このフックはレイアウト永続化に専念、telemetry の送信経路は呼び出し側の関心事)。
   */
  readonly onSwitch?: (next: LayoutKind) => void;
}

/**
 * 現在の viewport + pointer 特性を `isPhoneViewport` の入力として読む browser
 * アダプタ (SSR は PC 扱い = per-user)。スマホ判定を使う側 (この hook /
 * useRoomChrome の入室時フィット) はここを唯一の読み口にして判定を統一する。
 */
export const readPhoneViewportInput = (): PhoneViewportInput => {
  if (typeof window === 'undefined') {
    // SSR は PC 扱い (= per-user)
    return {
      width: Number.POSITIVE_INFINITY,
      height: Number.POSITIVE_INFINITY,
      coarsePointer: false,
    };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    coarsePointer:
      typeof window.matchMedia === 'function' &&
      window.matchMedia(COARSE_POINTER_MEDIA_QUERY).matches,
  };
};

export const useLayoutPreference = (
  opts: UseLayoutPreferenceOptions = {},
): UseLayoutPreferenceResult => {
  const storeRef = useRef<LayoutPreferenceStore>(opts.store ?? createLayoutPreferenceStore());
  const [stored, setStored] = useState<LayoutKind>(() => storeRef.current.get());
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    isPhoneViewport(readPhoneViewportInput()),
  );
  // §9 layout-switch analytics: onSwitch を ref に持つ (= setLayout の identity
  // を毎 render 変えないため — caller がインライン関数を渡しても setLayout の
  // useCallback 依存配列に onSwitch を直接入れない)。
  const onSwitchRef = useRef<((next: LayoutKind) => void) | undefined>(opts.onSwitch);
  onSwitchRef.current = opts.onSwitch;

  // viewport の変化を監視して isMobile を追従させる:
  //   - 幅・高さ (回転 / リサイズ) → resize イベント (スマホの回転は resize で届く)。
  //   - coarse pointer は端末特性なので初期評価 + matchMedia `change` リスナで十分
  //     (例: 2-in-1 でタブレットモード切替時に評価し直す)。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = (): void => {
      setIsMobile(isPhoneViewport(readPhoneViewportInput()));
    };
    update();
    window.addEventListener('resize', update);
    const mql =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(COARSE_POINTER_MEDIA_QUERY)
        : null;
    mql?.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      mql?.removeEventListener('change', update);
    };
  }, []);

  // 明示トグル: state 更新 + localStorage 書き込みを 1 経路に集約 (uiScalePreference 流儀)。
  // §9: この呼び出し箇所だけが「明示トグル」の瞬間 (= 毎 render でも、スマホ強制
  // Modern 解決 (`resolveLayout`) でもない) なので、layout-switch analytics の
  // 送信フックはここに置く。
  const setLayout = useCallback((next: LayoutKind): void => {
    setStored(next);
    storeRef.current.set(next);
    onSwitchRef.current?.(next);
  }, []);

  const layout = resolveLayout({ stored, isMobile });
  const showToggle = shouldShowLayoutToggle(isMobile);

  return { layout, setLayout, isMobile, showToggle };
};
